import { z } from "zod";

export const SEX_AT_BIRTH = ["female", "male", "intersex", "unknown"] as const;
export type SexAtBirth = (typeof SEX_AT_BIRTH)[number];
export const SEX_AT_BIRTH_LABELS: Record<SexAtBirth, string> = { female: "Female", male: "Male", intersex: "Intersex", unknown: "Prefer not to say" };

export const CLIENT_STATUSES = ["lead", "active", "archived", "merged"] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const CLIENT_SOURCES = ["staff", "booking", "walk_in", "import"] as const;
export type ClientSource = (typeof CLIENT_SOURCES)[number];

export const ADDRESS_KINDS = ["home", "residence", "business", "hotel", "other"] as const;
export type AddressKind = (typeof ADDRESS_KINDS)[number];
export const ADDRESS_KIND_LABELS: Record<AddressKind, string> = { home: "Home", residence: "Residence", business: "Business", hotel: "Hotel", other: "Other" };

export const CONSENT_CHANNELS = ["email_reminders", "sms_reminders", "email_marketing", "sms_marketing"] as const;
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number];
/** On until the client opts out: visit messages, and marketing emails (Boulevard clients opted in to them). Marketing texts need their own opt-in. */
export const CONSENT_ON_UNLESS_OPTED_OUT: readonly ConsentChannel[] = ["email_reminders", "sms_reminders", "email_marketing"];
export const CONSENT_LABELS: Record<ConsentChannel, string> = {
  email_reminders: "Email reminders",
  sms_reminders: "Text reminders",
  email_marketing: "Marketing emails",
  sms_marketing: "Marketing texts",
};

export const LINK_KINDS = ["guardian", "family"] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

/**
 * Store US numbers as +1XXXXXXXXXX so the same phone always matches.
 * Returns null for anything that isn't a plausible number.
 */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+") && digits.length >= 8 && digits.length <= 15) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/**
 * A US number formatted as it's typed: "405", "(405) 210", "(405) 210-7822". Punctuation only
 * appears before a digit, so backspace always removes a digit. A leading 1 is dropped; anything
 * else (international, or more than 10 digits) is left as typed.
 */
export function formatPhoneTyping(raw: string): string {
  if (raw.trim().startsWith("+") && !raw.trim().startsWith("+1")) return raw;
  let d = raw.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length > 10) return raw;
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Whole years between a date of birth and a date (both YYYY-MM-DD). */
/** Age on a date, or null when the birth date isn't known (imported clients; asked before they book). */
export const ageIfKnown = (dob: string | null, on: string): number | null => (dob ? ageOn(dob, on) : null);

export function ageOn(dob: string, on: string): number {
  const [by, bm, bd] = dob.split("-").map(Number) as [number, number, number];
  const [y, m, d] = on.split("-").map(Number) as [number, number, number];
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 1990-04-02");
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .default(null)
    .transform((v) => (v ? v : null));
const phone = z
  .string()
  .trim()
  .max(30)
  .nullable()
  .default(null)
  .transform((v, ctx) => {
    if (!v) return null;
    const n = normalizePhone(v);
    if (!n) ctx.addIssue({ code: "custom", message: "Enter a 10-digit phone number" });
    return n;
  });

export const clientAddressSchema = z.object({
  label: z.string().trim().max(40).default(""),
  kind: z.enum(ADDRESS_KINDS).default("home"),
  line1: z.string().trim().min(1).max(120),
  line2: z.string().trim().max(120).default(""),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(2).max(40),
  postalCode: z.string().trim().min(3).max(12),
  /** Gate codes, parking, which door. */
  notes: z.string().trim().max(300).default(""),
});
export type ClientAddressInput = z.infer<typeof clientAddressSchema>;

export const clientInputSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    preferredName: optionalText(80),
    /** Optional for someone on file only as a contact (a parent); needed before they can be booked. */
    dob: z.preprocess((v) => (v === "" || v === undefined ? null : v), isoDate.nullable()),
    sexAtBirth: z.enum(SEX_AT_BIRTH).nullable().default(null),
    pronouns: optionalText(40),
    mobile: phone,
    email: z
      .string()
      .trim()
      .toLowerCase()
      .max(254)
      .nullable()
      .default(null)
      .transform((v) => (v ? v : null))
      .pipe(z.string().email().nullable()),
    emergencyName: optionalText(120),
    emergencyRelationship: optionalText(60),
    emergencyPhone: phone,
    schedulingAlert: optionalText(200),
    addresses: z.array(clientAddressSchema).max(10).default([]),
  })
  .refine((v) => !v.dob || (v.dob <= new Date().toISOString().slice(0, 10) && v.dob >= "1900-01-01"), { message: "Check the date of birth", path: ["dob"] })
  // Every adult needs an email (good faith exams and receipts); minors can use their parent's. With no
  // date of birth (a contact only) it can wait: booking asks for both.
  .refine((v) => !!v.email || !v.dob || ageOn(v.dob, new Date().toISOString().slice(0, 10)) < 18, { message: "Add an email address", path: ["email"] });
export type ClientInput = z.infer<typeof clientInputSchema>;

/** Adults need an email on file; minors can use their parent's. */
export const needsEmail = (c: { email: string | null; dob: string | null }, today: string) => !c.email && (!c.dob || ageOn(c.dob, today) >= 18);

export interface ClientSummary {
  id: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  /** Null for some imported clients: needed before they can book. */
  dob: string | null;
  mobile: string | null;
  email: string | null;
  status: ClientStatus;
  tagIds: string[];
}

/** A page of the Clients table. */
export interface ClientTablePage {
  rows: (ClientSummary & { lastVisit: string | null })[];
  /** How many clients match, across all pages. */
  total: number;
}

export interface ClientAddress extends ClientAddressInput {
  id: string;
}

export interface ConsentState {
  channel: ConsentChannel;
  granted: boolean;
  /** Nothing on record: On until they opt out (all but marketing texts, which need their own opt-in). */
  assumed: boolean;
  recordedAt: string | null;
  method: string | null;
}

export interface ConsentEvent {
  channel: ConsentChannel;
  granted: boolean;
  method: string;
  wording: string;
  recordedBy: string | null;
  recordedAt: string;
}

export interface ClientNote {
  id: string;
  body: string;
  authorName: string | null;
  createdAt: string;
  correctsNoteId: string | null;
  /** Imported from the old system ("boulevard"); null = written here. */
  source: string | null;
  /** An allergy or medication note (from the old system). */
  kind: "allergy" | "medication" | null;
}

export interface ClientLink {
  id: string;
  kind: LinkKind;
  /** "guardian": the other person is this client's guardian. "ward": this client is the other person's guardian. "family": either way. */
  role: "guardian" | "ward" | "family";
  other: { id: string; firstName: string; lastName: string; dob: string | null; mobile: string | null };
}

export interface ClientDetail extends ClientSummary {
  sexAtBirth: SexAtBirth | null;
  pronouns: string | null;
  emergencyName: string | null;
  emergencyRelationship: string | null;
  emergencyPhone: string | null;
  schedulingAlert: string | null;
  source: ClientSource;
  mergedIntoId: string | null;
  /** Booked online as someone new who looks like this existing client: staff check, then merge or dismiss. */
  possibleDuplicateOf: { id: string; name: string; dob: string | null } | null;
  addresses: ClientAddress[];
  consents: ConsentState[];
  consentHistory: ConsentEvent[];
  notes: ClientNote[];
  links: ClientLink[];
  merges: { mergedClientId: string; mergedName: string; mergedAt: string; mergedBy: string | null }[];
  createdAt: string;
  updatedAt: string;
}

export interface DuplicateCandidate {
  client: ClientSummary;
  reasons: ("same_dob_name" | "same_mobile" | "same_email")[];
}

export const tagInputSchema = z.object({
  name: z.string().trim().min(1).max(40),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex color"),
  /** Adding it to a client counts as a good faith exam (e.g. "MD Approved"). */
  countsAsGfe: z.boolean().default(false),
  /** Only people who can add medical director tags may add or remove it. */
  mdOnly: z.boolean().default(false),
});

export interface TagItem {
  id: string;
  name: string;
  color: string;
  countsAsGfe: boolean;
  mdOnly: boolean;
  clientCount: number;
}

/** A client's last visit, offered when booking them again (one tap; never pre-chosen). */
export interface LastVisit {
  /** The visit's day (clinic time zone). */
  date: string;
  services: { serviceId: string; addOnIds: string[] }[];
}
